import { useState } from 'react'
import type { Violation } from '@project/contracts'
import { Button, Modal, Table, type ColumnDef as TableColumn } from '../design/index.js'

/**
 * How many offending rows are shown before `Show more` (docs/06 §"Rate exceeds
 * estimation", docs/15 R-5.1). A rule that fires on one line and a rule that
 * fires on ninety are the same dialog, and the second one buries its own
 * buttons off the bottom of the screen. The first few rows are enough to tell
 * somebody which rule they broke; the rest is for whoever wants to read it.
 */
const SHOWN_AT_FIRST = 5

export interface ExceptionDialogProps {
  readonly violations: readonly Violation[]
  readonly onFix: () => void
  readonly onOverride?: (() => void) | undefined
  readonly busy?: boolean
  /** Read-only view of exceptions already accepted on an approved document. */
  readonly historic?: boolean
  /** What a draft would raise if it were submitted now (BILL-051). */
  readonly preview?: boolean
}

interface OffendingRow {
  readonly key: string
  readonly cells: readonly string[]
}

const ViolationBlock = ({ violation }: { violation: Violation }) => {
  const [expanded, setExpanded] = useState(false)
  const columns: TableColumn<OffendingRow>[] = violation.columns.map((header, i) => ({
    key: String(i),
    header,
    render: (row) => row.cells[i] ?? '',
  }))
  const all: OffendingRow[] = violation.rows.map((cells, i) => ({ key: `${i}`, cells }))
  const hidden = all.length - SHOWN_AT_FIRST
  const rows = expanded ? all : all.slice(0, SHOWN_AT_FIRST)

  return (
    <section className="violation">
      <h3 className="violation__title">{violation.title}</h3>
      <p className="violation__why">{violation.explanation}</p>
      {rows.length > 0 ? <Table columns={columns} rows={rows} getKey={(row) => row.key} /> : null}
      {hidden > 0 ? (
        <button type="button" className="link-button" onClick={() => setExpanded(!expanded)}>
          {expanded ? 'Show fewer' : `Show more (${hidden} more ${hidden === 1 ? 'row' : 'rows'})`}
        </button>
      ) : null}
    </section>
  )
}

/**
 * The safe way out is the primary button. Overriding is deliberately the plain
 * one, and the consequence is spelled out rather than hidden in a tooltip: the
 * approver will read this same table.
 */
export const ExceptionDialog = ({ violations, onFix, onOverride, busy = false, historic = false, preview = false }: ExceptionDialogProps) => {
  const blocking = violations.some((v) => v.severity === 'BLOCK')
  const readOnly = historic || preview
  const canOverride = !readOnly && !blocking && onOverride !== undefined

  return (
    <Modal
      title={preview ? 'Needs attention' : historic ? 'Exceptions accepted on this document' : 'This document breaks a rule'}
      subtitle={
        preview
          ? 'Found on this draft. Nothing is stopped yet — submitting asks again.'
          : historic
            ? 'These were accepted when the document was submitted, and are kept with it.'
            : undefined
      }
      tone={blocking ? 'bad' : 'warn'}
      onClose={onFix}
      actions={
        <>
          {canOverride ? (
            <Button kind="ghost" disabled={busy} onClick={onOverride}>
              {busy ? 'Submitting…' : 'Submit anyway, with exceptions'}
            </Button>
          ) : null}
          <Button kind="primary" disabled={busy} onClick={onFix}>
            {preview ? 'Ok' : historic ? 'Close' : 'Go back and fix it'}
          </Button>
        </>
      }
    >
      {violations.map((v) => (
        <ViolationBlock key={`${v.rule}-${v.title}`} violation={v} />
      ))}

      {canOverride ? (
        <p style={{ marginTop: 24 }}>
          If you submit anyway, the document will be marked <strong>Approved with exceptions</strong>, and the approver
          sees exactly this list before deciding.
        </p>
      ) : null}

      {blocking && !readOnly ? (
        <p style={{ marginTop: 24 }}>This one cannot be overridden. Correct the rows above and submit again.</p>
      ) : null}
    </Modal>
  )
}
